import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMediaQuery, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import {remCalc} from '../../../theme/_utils'
import {SortingSliderTile} from './sorting-slider-tile'
import {calculateVerticalPadding} from '../utils'

export const SortingSlider = ({
    variant = 'white',
    hasPaddings = true,
    hasSlider,
    heading = '',
    bodycopy = '',
    sortingSliderGroup = [],
}) => {
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(449)})`)
    const verticalPadding = calculateVerticalPadding('', hasPaddings)

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('SortingSlider')

    const spacing_16 = remCalc(16)
    const spacing_48 = remCalc(48)
    const spacing_72 = remCalc(72)

    return (
        <Flex
            __css={{...styles.main}}
            data-testid="sorting-slider"
            my={verticalPadding}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
            className="del-sorting-slider"
        >
            <Flex {...styles.container}>
                {heading && (
                    <Text
                        as="h2"
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.heading}
                    >
                        {heading}
                    </Text>
                )}
                {bodycopy && (
                    <Box
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.bodycopy}
                        dangerouslySetInnerHTML={{__html: bodycopy}}
                    />
                )}
                {hasSlider ? (
                    <Box __css={swiperStyles} data-testid="sorting-slider-swiper">
                        <Swiper
                            key={isMobile ? 'mobile-view' : 'other-view'}
                            modules={[Navigation, Pagination, Keyboard]}
                            navigation={{
                                prevEl: `.srt-sld-swiper-button-prev`,
                                nextEl: `.srt-sld-swiper-button-next`,
                            }}
                            pagination={{
                                clickable: true,
                                el: '.swiper-pagination',
                            }}
                            keyboard={{
                                enabled: true,
                            }}
                            loop={
                                isMobile
                                    ? sortingSliderGroup.length > 1
                                    : sortingSliderGroup.length > 4
                            }
                            breakpoints={{
                                0: {
                                    slidesPerView: 1,
                                    enabled: sortingSliderGroup.length > 1,
                                    spaceBetween: 0,
                                },
                                768: {
                                    slidesPerView: 4,
                                    enabled: sortingSliderGroup.length > 4,
                                    spaceBetween: 16,
                                },
                                1024: {
                                    slidesPerView: 4,
                                    enabled: sortingSliderGroup.length > 4,
                                    spaceBetween: 24,
                                },
                            }}
                        >
                            {sortingSliderGroup.map((tile, tIndex) => (
                                <SwiperSlide key={`sorting-tile-${tIndex}`}>
                                    <SortingSliderTile
                                        styles={styles}
                                        tile={tile}
                                        tIndex={tIndex}
                                        variant={variant}
                                    />
                                </SwiperSlide>
                            ))}
                            <ChevronLeft
                                className={'srt-sld-swiper-button-prev'}
                                aria-label="swiper button prev icon"
                                {...styles.chevronIcon}
                                left={'0'}
                                borderColor={variant === 'black' ? 'text.inverse' : 'text.default'}
                            />
                            <ChevronRight
                                className={'srt-sld-swiper-button-next'}
                                aria-label="swiper button next icon"
                                {...styles.chevronIcon}
                                right={'0'}
                                borderColor={variant === 'black' ? 'text.inverse' : 'text.default'}
                            />
                            <Box className={'swiper-pagination'} />
                        </Swiper>
                    </Box>
                ) : (
                    <Flex data-testid="sorting-slider-noswiper" {...styles.tilecontainer}>
                        {sortingSliderGroup.map((tile, tIndex) => (
                            <Box
                                key={`sorting-tile-${tIndex}`}
                                w={{
                                    base: '100%',
                                    sm: `calc((100% - ${spacing_16}) / 2)`,
                                    md: `calc((100% - ${spacing_48}) / 3)`,
                                    lg: `calc((100% - ${spacing_72}) / 4)`,
                                }}
                            >
                                <SortingSliderTile
                                    styles={styles}
                                    tile={tile}
                                    tIndex={tIndex}
                                    variant={variant}
                                />
                            </Box>
                        ))}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

SortingSlider.propTypes = {
    hasPaddings: PropTypes.bool,
    hasSlider: PropTypes.bool,
    variant: PropTypes.string,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingSliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            image: PropTypes.shape(),
            features: PropTypes.arrayOf(PropTypes.shape({feature: PropTypes.string})),
            button: PropTypes.shape(),
        }),
    ),
}
